/**
 * Messages — the real one.
 *
 * This replaces the demo page that showed fabricated threads. Two panes:
 * conversations (from the comm hub) on the left, the open thread on the
 * right. Every thread that belongs to a campaign says so and links there —
 * threads anchor to the transaction, never float. Replies queue in the
 * outbox for approval; nothing sends until dispatch is enabled.
 *
 * Deep link: /messages?c=<conversationId> opens that thread directly (Home's
 * inbox rows land here).
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox, Send, Megaphone, ClipboardList, MessageSquare } from 'lucide-react';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { isCoworkLive } from '@/cowork/live/coworkClient';
import { cn } from '@/components/ui/utils';

const INBOX_TINT: Record<string, string> = {
  'Task Threads': 'bg-[#2a5fb0]/10 text-[#2a5fb0]',
  'Agent Chat': 'bg-[#0d8a5f]/10 text-[#0d8a5f]',
  'System Notifications': 'bg-gray-100 text-gray-500',
  'User Messaging': 'bg-[#be43ff]/10 text-[#be43ff]',
};

export function MessagesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<number | null>(null);
  const [reply, setReply] = useState('');
  const [queued, setQueued] = useState(false);

  const { data: me } = useQuery({
    queryKey: ['cowork', 'cm', 'me'],
    queryFn: () => coworkApi.cm.me().catch(() => ({ staff: false, email: null })),
    enabled: isCoworkLive(),
    staleTime: 10 * 60_000,
    retry: false,
  });
  const staff = me?.staff === true;

  const { data: inbox, isLoading } = useQuery({
    queryKey: ['cowork', 'cm', 'inbox'],
    queryFn: () => coworkApi.cm.inbox(),
    enabled: staff,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  // Deep link: ?c=<id> selects a conversation on arrival.
  useEffect(() => {
    const c = new URLSearchParams(location.search).get('c');
    if (c && !Number.isNaN(Number(c))) setSelected(Number(c));
  }, [location.search]);

  const conversations = inbox?.conversations ?? [];
  useEffect(() => {
    if (selected === null && conversations.length > 0) setSelected(conversations[0].id);
  }, [conversations, selected]);

  const { data: thread } = useQuery({
    queryKey: ['cowork', 'cm', 'inbox', selected],
    queryFn: () => coworkApi.cm.inboxMessages(selected!),
    enabled: staff && selected !== null,
    refetchInterval: 45_000,
    staleTime: 20_000,
  });

  const active = conversations.find(c => c.id === selected);

  async function sendReply() {
    if (!reply.trim() || !active) return;
    await coworkApi.cm.inboxReply(active.id, reply.trim(), active.title);
    setReply('');
    setQueued(true);
    void qc.invalidateQueries({ queryKey: ['cowork', 'cm', 'outbox'] });
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa] p-2.5">
      <div className="min-h-[calc(100vh-20px)] overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h1 className="flex items-center gap-2 text-xl text-[#101828]" style={{ fontWeight: 700 }}>
            <MessageSquare className="h-5 w-5 text-[#be43ff]" aria-hidden /> Messages
          </h1>
          <p className="mt-0.5 text-[13px] text-[#667085]">
            Every thread in one place. Threads that belong to a campaign link to it; replies wait for your approval before anything sends.
          </p>
        </div>

        {!staff ? (
          <div className="flex flex-col items-center gap-2 py-24 text-center">
            <Inbox className="h-8 w-8 text-gray-300" aria-hidden />
            <p className="text-sm text-[#344054]" style={{ fontWeight: 600 }}>Messaging is arriving with your campaigns</p>
            <p className="max-w-md text-[13px] text-[#98a2b3]">
              Campaign conversations will land here as your campaigns run. Nothing to see yet, and nothing fake to look at either.
            </p>
          </div>
        ) : (
          <div className="flex" style={{ height: 'calc(100vh - 118px)' }}>
            {/* Conversations */}
            <div className="w-[340px] shrink-0 overflow-y-auto border-r border-gray-100">
              {isLoading && <p className="p-4 text-[13px] text-[#98a2b3]">Loading conversations…</p>}
              {!isLoading && conversations.length === 0 && (
                <p className="p-4 text-[13px] text-[#98a2b3]">
                  {inbox?.connected === false ? 'The comm hub is not connected in this environment.' : 'No conversations yet.'}
                </p>
              )}
              {conversations.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { setSelected(c.id); setQueued(false); navigate(`/messages?c=${c.id}`, { replace: true }); }}
                  className={cn(
                    'block w-full border-b border-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-50',
                    selected === c.id && 'bg-[#be43ff]/[0.05]',
                  )}
                >
                  <div className="mb-0.5 flex items-center gap-1.5">
                    <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold', INBOX_TINT[c.inbox] ?? 'bg-gray-100 text-gray-500')}>{c.inbox}</span>
                    <span className="min-w-0 flex-1 truncate text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{c.title}</span>
                    {c.unread > 0 && <span className="rounded-full bg-[#be43ff] px-1.5 text-[10px] font-bold text-white">{c.unread}</span>}
                  </div>
                  {c.lastMessage && <p className="truncate text-[12px] text-[#98a2b3]">{c.lastMessage}</p>}
                  {c.campaignId && (
                    <span className="mt-1 inline-flex items-center gap-1 rounded bg-[#be43ff]/8 px-1.5 py-0.5 text-[10px] font-semibold text-[#be43ff]">
                      <Megaphone className="h-2.5 w-2.5" aria-hidden /> campaign thread
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Thread */}
            <div className="flex min-w-0 flex-1 flex-col">
              {active ? (
                <>
                  <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-2.5">
                    <span className="min-w-0 flex-1 truncate text-[14px] text-[#101828]" style={{ fontWeight: 600 }}>{active.title}</span>
                    {active.campaignId && (
                      <button
                        type="button"
                        onClick={() => navigate(`/campaigns/${active.campaignId}`)}
                        className="inline-flex items-center gap-1 rounded-lg bg-[#be43ff]/10 px-2.5 py-1 text-[12px] text-[#be43ff] transition-colors hover:bg-[#be43ff]/20"
                        style={{ fontWeight: 600 }}
                      >
                        <Megaphone className="h-3 w-3" aria-hidden /> Open campaign
                      </button>
                    )}
                    {active.taskId && (
                      <button
                        type="button"
                        onClick={() => navigate('/agents/tasks')}
                        className="inline-flex items-center gap-1 rounded-lg bg-[#2a5fb0]/10 px-2.5 py-1 text-[12px] text-[#2a5fb0] transition-colors hover:bg-[#2a5fb0]/20"
                        style={{ fontWeight: 600 }}
                      >
                        <ClipboardList className="h-3 w-3" aria-hidden /> Open task
                      </button>
                    )}
                  </div>
                  <div className="flex-1 space-y-2 overflow-y-auto px-5 py-4">
                    {(thread?.messages ?? []).map(m => (
                      <div key={m.id} className={cn('max-w-[72%] rounded-xl px-3.5 py-2 text-[13px]',
                        m.outgoing ? 'ml-auto bg-[#2a5fb0]/8 text-[#1d3f75]' : 'bg-[#f8f9fa] text-[#344054]')}>
                        {m.senderName && <p className="mb-0.5 text-[10.5px] text-[#98a2b3]" style={{ fontWeight: 600 }}>{m.senderName}{m.private ? ' · internal note' : ''}</p>}
                        <p className="whitespace-pre-wrap">{m.content}</p>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-100 px-5 py-3">
                    <div className="flex gap-2">
                      <input
                        value={reply}
                        onChange={e => setReply(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void sendReply(); } }}
                        placeholder="Reply: queues in your outbox for approval"
                        className="min-w-0 flex-1 rounded-xl border border-gray-200 px-3.5 py-2 text-[13px] outline-none focus:border-[#be43ff]"
                      />
                      <button type="button" onClick={() => void sendReply()}
                        className="rounded-xl bg-[#be43ff] px-3.5 text-white transition-colors hover:bg-[#a832e6]" aria-label="Queue reply">
                        <Send className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                    {queued && <p className="mt-1.5 text-[12px] text-[#12b76a]">Queued in your outbox for approval: nothing sent yet.</p>}
                  </div>
                </>
              ) : (
                <div className="flex flex-1 items-center justify-center text-[13px] text-[#98a2b3]">Pick a conversation</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
